import { MAX_ITEM_REQUESTS_PER_SECOND } from "odyssey-shared";

/**
 * How many item requests the page sends (P2.8-T11, ADR-091 decision 10, PROTOCOL.md's item request rate). The map
 * server counts `useItem`, `bagMove` and `lootTake` together on one window and drops a request past
 * `MAX_ITEM_REQUESTS_PER_SECOND` with no answer, so a request the page sent over the limit would leave its slot
 * waiting for an answer that never comes. The page therefore never sends one the server would drop: it keeps its own
 * count over any one second, one under the server's limit so a burst the network bunches together still fits, and a
 * request past it is simply not sent, which its caller sees and marks nothing pending for.
 */

/**
 * The length of the page's window, in ms: the server's second.
 */
export const ITEM_REQUEST_WINDOW_MS = 1000;

/**
 * The most item requests the page sends in any window: one fewer than the server allows.
 */
export const ITEM_REQUESTS_PER_WINDOW = MAX_ITEM_REQUESTS_PER_SECOND - 1;

/**
 * The page's count of item requests over the last window, one per connection.
 *
 * @example
 * ```ts
 * const budget = new ItemRequestBudget();
 * if (budget.take(performance.now())) {
 *   socket.send(frame);
 * }
 * ```
 */
export class ItemRequestBudget {
  private readonly sent: number[] = [];

  /**
   * Asks to send one request now, counting it when there is room.
   *
   * @param nowMs The page clock.
   * @returns True when the request may be sent, and has been counted; false when the window is full, which counts
   *   nothing.
   */
  public take(nowMs: number): boolean {
    while (this.sent.length > 0 && nowMs - (this.sent[0] as number) >= ITEM_REQUEST_WINDOW_MS) {
      this.sent.shift();
    }
    if (this.sent.length >= ITEM_REQUESTS_PER_WINDOW) {
      return false;
    }
    this.sent.push(nowMs);
    return true;
  }
}
